<template>
  <v-container fluid class="pl-4 pr-4 pb-4 pt-1">
    <!-- Header with actions and filter toggle -->
    <v-row align="center" class="mb-0 px-0" style="min-height: 48px;">
      <v-col cols="auto">
        <v-btn 
          color="primary"
          @click="openCreateDialog"
          prepend-icon="mdi-plus"
        >
          Добавить
        </v-btn>
        <v-btn
          v-if="selectedItems.length > 0"
          color="error"
          variant="outlined"
          class="ml-2"
          @click="bulkDelete"
          prepend-icon="mdi-delete"
        >
          Удалить ({{ selectedItems.length }})
        </v-btn>
      </v-col>
      <v-col class="text-center">
        <h4 class="mb-0">Авторы</h4>
      </v-col>
      <v-col cols="auto">
        <!-- Pagination controls with total count -->
        <div class="d-flex align-center ga-2">
          <span class="text-caption">На странице:</span>
          <v-select
            v-model="limit"
            :items="[5, 10, 20, 50, 100, 250, 500]"
            variant="outlined"
            density="compact"
            hide-details
            style="max-width: 160px"
            @update:model-value="onPageChange(1)"
          ></v-select>
          <v-pagination
            v-model="currentPage"
            :length="totalPages"
            :total-visible="5"
            size="small"
            @update:model-value="onPageChange"
          ></v-pagination>
          <span class="text-caption font-weight-bold"> Найдено: {{ totalItems }}</span>
        </div>
      </v-col>
      <v-col cols="auto">
        <v-btn
          :icon="showFilters ? 'mdi-filter-off' : 'mdi-filter'"
          variant="outlined"
          @click="toggleFilters"
          :color="showFilters ? 'primary' : 'default'"
        >
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <!-- Main table column -->
      <v-col :cols="showFilters ? 10 : 12" class="table-column-transition">
        <!-- Table section -->
        <v-card>
          <v-card-text class="pa-0">
            <v-data-table
              :headers="headers"
              :items="items"
              :loading="loading"
              :items-per-page="limit"
              class="elevation-1"
              hover
              fixed-header
              height="calc(100vh - 236px)"
              show-select
              v-model="selectedItems"
              item-value="id"
              hide-default-footer
            >
              <!-- Custom table columns -->
              <template v-slot:[`item.name`]="{ item }">
                <div class="font-weight-medium">{{ item.name }}</div>
              </template>

              <!-- Kind -->
              <template v-slot:[`item.kind`]="{ item }">
                <v-chip
                  :color="getKindColor(item.kind)"
                  variant="tonal"
                  size="small"
                >
                  {{ getKindText(item.kind) }}
                </v-chip>
              </template>

              <!-- Gender -->
              <template v-slot:[`item.gender`]="{ item }">
                <v-chip
                  variant="outlined"
                  size="small"
                >
                  {{ getGenderText(item.gender) }}
                </v-chip>
              </template>

              <!-- Age -->
              <template v-slot:[`item.age`]="{ item }">
                {{ item.age || '—' }}
              </template>

              <!-- Followers -->
              <template v-slot:[`item.followers`]="{ item }">
                <div class="text-right">
                  {{ formatNumber(item.followers) }}
                </div>
              </template>

              <!-- Verification status -->
              <template v-slot:[`item.is_verified`]="{ item }">
                <v-chip
                  :color="item.is_verified ? 'success' : 'default'"
                  variant="tonal"
                  size="small"
                >
                  <v-icon start size="16">
                    {{ item.is_verified ? 'mdi-check-circle' : 'mdi-cancel' }}
                  </v-icon>
                  {{ item.is_verified ? 'Верифицирован' : 'Не верифицирован' }}
                </v-chip>
              </template>

              <!-- Actions column -->
              <template v-slot:[`item.actions`]="{ item }">
                <v-btn
                  size="small"
                  variant="text"
                  icon="mdi-pencil"
                  @click="editItem(item)"
                ></v-btn>
                <v-btn
                  size="small"
                  variant="text"
                  icon="mdi-delete"
                  @click="deleteItemConfirm(item)"
                ></v-btn>
              </template>

              <!-- No data slot -->
              <template v-slot:no-data>
                <v-container>
                  <v-row justify="center">
                    <v-col cols="auto">
                      <v-icon size="48" color="grey">mdi-account-group</v-icon>
                      <p class="text-h6 text-grey">Авторы не найдены</p>
                    </v-col>
                  </v-row>
                </v-container>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Filters column with transition -->
      <v-slide-x-reverse-transition>
        <v-col v-if="showFilters" cols="2" key="filters">
          <v-card>
            <v-card-title class="d-flex justify-space-between align-center">
              <span>Фильтры</span>
              <v-btn
                icon="mdi-close"
                variant="text"
                size="small"
                @click="toggleFilters"
              ></v-btn>
            </v-card-title>
            <v-card-text>
            <v-text-field
              v-model="filters.name"
              label="Имя автора"
              placeholder="Поиск по имени..."
              prepend-inner-icon="mdi-magnify"
              density="compact"
              clearable
              @input="debouncedSearch"
            ></v-text-field>

            <v-select
              v-model="filters.kind"
              :items="kindOptions"
              label="Тип автора"
              prepend-inner-icon="mdi-account-box"
              density="compact"
              clearable
              @update:model-value="onFilterChange"
            ></v-select>

            <v-select
              v-model="filters.gender"
              :items="genderOptions"
              label="Пол"
              prepend-inner-icon="mdi-gender-male-female"
              density="compact"
              clearable
              @update:model-value="onFilterChange"
            ></v-select>

            <v-select
              v-model="filters.is_verified"
              :items="verificationOptions"
              label="Статус верификации"
              prepend-inner-icon="mdi-check-circle"
              density="compact"
              clearable
              @update:model-value="onFilterChange"
            ></v-select>

            <v-text-field
              v-model="filters.followers_from"
              label="Подписчиков от"
              type="number"
              prepend-inner-icon="mdi-account-multiple"
              density="compact"
              clearable
              @input="debouncedSearch"
            ></v-text-field>

            <v-text-field
              v-model="filters.followers_to"
              label="Подписчиков до"
              type="number"
              prepend-inner-icon="mdi-account-multiple"
              density="compact"
              clearable
              @input="debouncedSearch"
            ></v-text-field>

            <v-divider class="my-3"></v-divider>

            <v-btn
              color="primary"
              variant="outlined"
              block
              @click="resetFilters"
              prepend-icon="mdi-filter-remove"
            >
              Сбросить фильтры
            </v-btn>

            <v-chip
              v-if="hasActiveFilters"
              color="primary"
              variant="outlined"
              class="mt-3"
            >
              Активных фильтров: {{ activeFiltersCount }}
            </v-chip>
          </v-card-text>
        </v-card>
      </v-col>
    </v-slide-x-reverse-transition>
  </v-row>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="dialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ editedIndex === -1 ? 'Новый автор' : 'Редактировать автора' }}</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="editedItem.name"
                  label="Имя автора"
                  placeholder="Имя автора..."
                  :rules="nameRules"
                  required
                  density="compact"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-select
                  v-model="editedItem.kind"
                  :items="kindOptions"
                  label="Тип автора"
                  :rules="kindRules"
                  required
                  density="compact"
                ></v-select>
              </v-col>
              <v-col cols="12">
                <v-select
                  v-model="editedItem.gender"
                  :items="genderOptions"
                  label="Пол"
                  :rules="genderRules"
                  required
                  density="compact"
                ></v-select>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="editedItem.age"
                  label="Возраст"
                  type="number"
                  placeholder="Оставьте пустым если неизвестно"
                  :rules="ageRules"
                  density="compact"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="editedItem.followers"
                  label="Количество подписчиков"
                  type="number"
                  placeholder="0"
                  :rules="followersRules"
                  density="compact"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-switch
                  v-model="editedItem.is_verified"
                  label="Верифицирован"
                  color="primary"
                  hide-details
                ></v-switch>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue-darken-1" variant="text" @click="closeDialog">Отмена</v-btn>
          <v-btn color="blue-darken-1" variant="text" @click="save">Сохранить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="500px">
      <v-card>
        <v-card-title class="text-h5">Подтверждение удаления</v-card-title>
        <v-card-text>Вы уверены, что хотите удалить выбранные элементы?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue-darken-1" variant="text" @click="deleteDialog = false">Отмена</v-btn>
          <v-btn color="error" variant="text" @click="deleteItem">Удалить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snackbar" :timeout="3000" :color="snackbarColor">
      {{ snackbarText }}
      <template v-slot:actions>
        <v-btn color="white" variant="text" @click="snackbar = false">Закрыть</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { referenceApi } from '@/services/api/referenceApi'
import { debounce } from 'lodash'

export default {
  name: 'AuthorsTable',
  setup() {
    // Data
    const items = ref([])
    const loading = ref(false)
    const totalItems = ref(0)
    const currentPage = ref(1)
    const limit = ref(25)
    const selectedItems = ref([])
    const showFilters = ref(localStorage.getItem('authors-filters-panel') !== 'false')

    // Dialog
    const dialog = ref(false)
    const deleteDialog = ref(false)
    const editedIndex = ref(-1)
    const editedItem = ref({
      name: '',
      kind: 'personal',
      is_verified: false,
      gender: 'unknown',
      age: null,
      followers: 0,
      attrs: {}
    })
    const defaultItem = {
      name: '',
      kind: 'personal',
      is_verified: false,
      gender: 'unknown',
      age: null,
      followers: 0,
      attrs: {}
    }
    const itemToDelete = ref(null)

    // Snackbar
    const snackbar = ref(false)
    const snackbarText = ref('')
    const snackbarColor = ref('success')

    // Headers
    const headers = [
      { 
        title: 'Имя', 
        key: 'name', 
        sortable: true,
        width: '25%'
      },
      { 
        title: 'Тип', 
        key: 'kind', 
        sortable: true,
        width: '15%'
      },
      { 
        title: 'Пол', 
        key: 'gender', 
        sortable: true,
        width: '10%'
      },
      { 
        title: 'Возраст', 
        key: 'age', 
        sortable: true,
        width: '10%'
      },
      { 
        title: 'Подписчики', 
        key: 'followers', 
        sortable: true,
        align: 'end',
        width: '15%'
      },
      { 
        title: 'Верификация', 
        key: 'is_verified', 
        sortable: true,
        width: '15%'
      },
      { title: 'Действия', key: 'actions', sortable: false, width: '120px' }
    ]

    // Filters
    const filters = ref({
      name: '',
      kind: null,
      gender: null,
      is_verified: null,
      followers_from: null,
      followers_to: null
    })

    // Filter options
    const verificationOptions = [
      { title: 'Не верифицирован', value: false },
      { title: 'Верифицирован', value: true }
    ]

    const kindOptions = [
      { title: 'Персональный', value: 'personal' },
      { title: 'Сообщество', value: 'community' },
      { title: 'Медиа аккаунт', value: 'media_account' },
      { title: 'Организация', value: 'organization' }
    ]

    const genderOptions = [
      { title: 'Мужской', value: 'male' },
      { title: 'Женский', value: 'female' },
      { title: 'Неизвестно', value: 'unknown' }
    ]

    // Computed
    const totalPages = computed(() => Math.ceil(totalItems.value / limit.value))
    
    const hasActiveFilters = computed(() => {
      return !!(
        filters.value.name || 
        filters.value.kind || 
        filters.value.gender || 
        filters.value.is_verified !== null ||
        filters.value.followers_from ||
        filters.value.followers_to
      )
    })

    const activeFiltersCount = computed(() => {
      let count = 0
      if (filters.value.name) count++
      if (filters.value.kind) count++
      if (filters.value.gender) count++
      if (filters.value.is_verified !== null) count++
      if (filters.value.followers_from) count++
      if (filters.value.followers_to) count++
      return count
    })

    // Validation rules
    const nameRules = [
      v => !!v || 'Имя автора обязательно',
      v => (v && v.length >= 1 && v.length <= 255) || 'Имя должно содержать от 1 до 255 символов'
    ]

    const kindRules = [
      v => !!v || 'Тип автора обязателен'
    ]

    const genderRules = [
      v => !!v || 'Пол обязателен'
    ]

    const ageRules = [
      v => v === null || v === '' || (!isNaN(v) && v >= 0 && v <= 150) || 'Возраст должен быть от 0 до 150 лет'
    ]

    const followersRules = [
      v => v === null || v === '' || v === 0 || (!isNaN(v) && v >= 0) || 'Количество подписчиков должно быть положительным числом'
    ]

    // Methods
    const loadData = async () => {
      loading.value = true
      try {
        const skip = (currentPage.value - 1) * limit.value
        const params = {
          skip,
          limit: limit.value,
          ...getActiveFilters()
        }
        const response = await referenceApi.getAuthors(params)
        items.value = response.data.items
        totalItems.value = response.data.total
      } catch (error) {
        console.error('Error loading authors:', error)
        showNotification('Ошибка при загрузке авторов', 'error')
      } finally {
        loading.value = false
      }
    }

    const getActiveFilters = () => {
      const activeFilters = {}
      if (filters.value.name) activeFilters.name = filters.value.name
      if (filters.value.kind) activeFilters.kind = filters.value.kind
      if (filters.value.gender) activeFilters.gender = filters.value.gender
      if (filters.value.is_verified !== null) activeFilters.is_verified = filters.value.is_verified
      if (filters.value.followers_from) activeFilters.followers_from = parseInt(filters.value.followers_from)
      if (filters.value.followers_to) activeFilters.followers_to = parseInt(filters.value.followers_to)
      return activeFilters
    }

    const onPageChange = (page) => {
      currentPage.value = page
      loadData()
    }

    const toggleFilters = () => {
      showFilters.value = !showFilters.value
      localStorage.setItem('authors-filters-panel', showFilters.value)
    }

    const resetFilters = () => {
      filters.value = {
        name: '',
        kind: null,
        gender: null,
        is_verified: null,
        followers_from: null,
        followers_to: null
      }
      onFilterChange()
    }

    const onFilterChange = () => {
      currentPage.value = 1
      loadData()
    }

    const debouncedSearch = debounce(() => {
      onFilterChange()
    }, 500)

    const openCreateDialog = () => {
      editedIndex.value = -1
      editedItem.value = Object.assign({}, defaultItem)
      dialog.value = true
    }

    const editItem = (item) => {
      editedIndex.value = items.value.indexOf(item)
      editedItem.value = Object.assign({}, item)
      dialog.value = true
    }

    const closeDialog = () => {
      dialog.value = false
      setTimeout(() => {
        editedItem.value = Object.assign({}, defaultItem)
        editedIndex.value = -1
      }, 300)
    }

    const save = async () => {
      try {
        // Prepare data for API
        const dataToSend = { ...editedItem.value }
        
        // Convert age and followers to numbers if provided
        if (dataToSend.age !== null && dataToSend.age !== '') {
          dataToSend.age = parseInt(dataToSend.age) || null
        }
        if (dataToSend.followers !== null && dataToSend.followers !== '') {
          dataToSend.followers = parseInt(dataToSend.followers) || 0
        }
        
        // Ensure default values
        if (!dataToSend.kind) {
          dataToSend.kind = 'personal'
        }
        if (!dataToSend.gender) {
          dataToSend.gender = 'unknown'
        }
        if (dataToSend.is_verified === undefined) {
          dataToSend.is_verified = false
        }
        if (!dataToSend.attrs) {
          dataToSend.attrs = {}
        }

        if (editedIndex.value > -1) {
          // Update
          await referenceApi.updateAuthor(dataToSend.id, dataToSend)
          showNotification('Автор успешно обновлен', 'success')
        } else {
          // Create
          await referenceApi.createAuthor(dataToSend)
          showNotification('Автор успешно создан', 'success')
        }
        closeDialog()
        loadData()
      } catch (error) {
        console.error('Error saving author:', error)
        const message = error.response?.data?.detail || 'Ошибка при сохранении автора'
        showNotification(message, 'error')
      }
    }

    const deleteItemConfirm = (item) => {
      itemToDelete.value = item ? [item.id] : selectedItems.value.map(i => i.id)
      deleteDialog.value = true
    }

    const deleteItem = async () => {
      try {
        const idsToDelete = itemToDelete.value || selectedItems.value.map(i => i.id)
        await Promise.all(idsToDelete.map(id => referenceApi.deleteAuthor(id)))
        showNotification('Авторы успешно удалены', 'success')
        deleteDialog.value = false
        selectedItems.value = []
        loadData()
      } catch (error) {
        console.error('Error deleting authors:', error)
        showNotification('Ошибка при удалении авторов', 'error')
      }
    }

    const bulkDelete = () => {
      deleteItemConfirm(null)
    }

    const showNotification = (text, color = 'success') => {
      snackbarText.value = text
      snackbarColor.value = color
      snackbar.value = true
    }

    const formatNumber = (value) => {
      if (value == null) return '0'
      return value.toLocaleString('ru-RU')
    }

    const getKindText = (kind) => {
      const texts = {
        'personal': 'Персональный',
        'community': 'Сообщество',
        'media_account': 'Медиа аккаунт',
        'organization': 'Организация'
      }
      return texts[kind] || kind
    }

    const getKindColor = (kind) => {
      const colors = {
        'personal': 'blue',
        'community': 'green',
        'media_account': 'purple',
        'organization': 'orange'
      }
      return colors[kind] || 'grey'
    }

    const getGenderText = (gender) => {
      const texts = {
        'male': 'Мужской',
        'female': 'Женский',
        'unknown': 'Неизвестно'
      }
      return texts[gender] || gender
    }

    // Watchers
    watch(limit, () => {
      currentPage.value = 1
      loadData()
    })

    // Lifecycle
    onMounted(() => {
      loadData()
    })

    return {
      // Data
      items,
      loading,
      totalItems,
      currentPage,
      limit,
      selectedItems,
      showFilters,
      headers,
      filters,
      verificationOptions,
      kindOptions,
      genderOptions,
      
      // Dialog
      dialog,
      deleteDialog,
      editedIndex,
      editedItem,
      
      // Snackbar
      snackbar,
      snackbarText,
      snackbarColor,
      
      // Computed
      totalPages,
      hasActiveFilters,
      activeFiltersCount,
      
      // Validation
      nameRules,
      kindRules,
      genderRules,
      ageRules,
      followersRules,
      
      // Methods
      onPageChange,
      toggleFilters,
      resetFilters,
      onFilterChange,
      debouncedSearch,
      openCreateDialog,
      editItem,
      closeDialog,
      save,
      deleteItemConfirm,
      deleteItem,
      bulkDelete,
      formatNumber,
      getKindText,
      getKindColor,
      getGenderText
    }
  }
}
</script>

<style scoped>
.table-column-transition {
  transition: flex-basis 0.3s ease, max-width 0.3s ease;
}
</style>